import React, { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import {
  computeEntrySummary,
  formatDateLabel,
  formatNumber,
  formatPercent,
} from "./utils";

const SUMMARY_CELL_SX = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const SUMMARY_LABEL_SX = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 700,
  color: "#64748B",
  lineHeight: 1.1,
  mb: 0.35,
};

const SUMMARY_VALUE_SX = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#1F2937",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const RentalSurveySummaryHeader = ({ entry, actions = null, onSurveyDateChange }) => {
  const summary = computeEntrySummary(entry);
  const [isEditingSurveyDate, setIsEditingSurveyDate] = useState(false);
  const [surveyDateDraft, setSurveyDateDraft] = useState(entry?.surveyDate || "");

  useEffect(() => {
    if (!isEditingSurveyDate) {
      setSurveyDateDraft(entry?.surveyDate || "");
    }
  }, [entry?.surveyDate, isEditingSurveyDate]);

  const commitSurveyDate = () => {
    setIsEditingSurveyDate(false);

    if (typeof onSurveyDateChange !== "function") return;

    const nextValue = surveyDateDraft || null;
    const currentValue = entry?.surveyDate || null;

    if (nextValue !== currentValue) {
      onSurveyDateChange(nextValue);
    }
  };

  const cancelSurveyDateEdit = () => {
    setSurveyDateDraft(entry?.surveyDate || "");
    setIsEditingSurveyDate(false);
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #F2F4F7 0%, #E8EDF2 100%)",
        borderBottom: "1px solid #D9E1EA",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(5, minmax(0, 1fr))",
          },
        }}
      >
        <Box sx={SUMMARY_CELL_SX}>
          <Typography sx={SUMMARY_LABEL_SX}>Surveyed</Typography>
          {isEditingSurveyDate ? (
            <TextField
              type="date"
              size="small"
              value={surveyDateDraft}
              autoFocus
              onChange={(event) => setSurveyDateDraft(event.target.value)}
              onBlur={commitSurveyDate}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitSurveyDate();
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancelSurveyDateEdit();
                }
              }}
              inputProps={{ style: { fontSize: "12px", padding: "6px 8px" } }}
              sx={{
                maxWidth: 165,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: 1,
                },
              }}
            />
          ) : (
            <Typography
              sx={{ ...SUMMARY_VALUE_SX, cursor: onSurveyDateChange ? "text" : "default" }}
              onDoubleClick={() => {
                if (!onSurveyDateChange) return;
                setSurveyDateDraft(entry?.surveyDate || "");
                setIsEditingSurveyDate(true);
              }}
              title={onSurveyDateChange ? "Double-click to edit" : undefined}
            >
              {formatDateLabel(entry?.surveyDate)}
            </Typography>
          )}
        </Box>

        <Box sx={SUMMARY_CELL_SX}>
          <Typography sx={SUMMARY_LABEL_SX}>Unit Mix</Typography>
          <Typography sx={SUMMARY_VALUE_SX}>{summary.unitMixLabel}</Typography>
        </Box>

        <Box sx={SUMMARY_CELL_SX}>
          <Typography sx={SUMMARY_LABEL_SX}>Total SF</Typography>
          <Typography sx={SUMMARY_VALUE_SX}>{formatNumber(summary.totalSF, { maximumFractionDigits: 0 })}</Typography>
        </Box>

        <Box sx={SUMMARY_CELL_SX}>
          <Typography sx={SUMMARY_LABEL_SX}>Occupancy %</Typography>
          <Typography sx={SUMMARY_VALUE_SX}>{formatPercent(summary.occupancyPercent)}</Typography>
        </Box>

        <Box sx={SUMMARY_CELL_SX}>
          <Typography sx={SUMMARY_LABEL_SX}>Average Occupancy %</Typography>
          <Typography sx={SUMMARY_VALUE_SX}>{formatPercent(summary.averageOccupancyPercent)}</Typography>
        </Box>
      </Box>

      {actions ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, pl: 0.5 }}>
          {actions}
        </Box>
      ) : null}
    </Box>
  );
};

export default RentalSurveySummaryHeader;
